import * as THREE from 'three';
import { C } from '../core/palette.js';
import { box, cyl, sphere } from '../core/meshkit.js';

// The Dancing Fruit Yogurt trio — the café's house band. Three yogurt-cup folks
// (Strawberry on vocals, Blueberry on keys, Banana on guitar) that bob to the
// beat. Build once, add band.root to the scene, and call update(dt, phase) each
// frame with the Groove's fractional beat phase so they groove in time.

function yogurtMember({ x, fruit, accent, instrument }){
  const g = new THREE.Group();
  g.position.set(x, 0, 0);

  // cup: tapered cream body
  cyl(0.42, 0.34, 1.0, C.cream, 0, 0.5, 0, g, 18);
  // a colored band/label around the cup
  cyl(0.44, 0.44, 0.22, accent, 0, 0.42, 0, g, 18);
  // swirl of yogurt on top
  const swirl = sphere(0.4, C.cream, 0, 1.06, 0, g, 16);
  swirl.scale.set(1, 0.7, 1);

  // the fruit topper (the character's identity)
  const topper = new THREE.Group(); topper.position.set(0, 1.4, 0); g.add(topper);
  if (fruit === 'strawberry'){
    const s = sphere(0.26, C.raspberry, 0, 0, 0, topper, 14); s.scale.set(1, 1.15, 1);
    sphere(0.05, C.sprout, 0, 0.26, 0, topper, 8);            // leafy top
    sphere(0.03, C.cream, -0.1, -0.05, 0.22, topper, 6);      // a seed fleck
  } else if (fruit === 'blueberry'){
    sphere(0.2, C.cobalt, -0.1, 0, 0, topper, 12);
    sphere(0.22, C.cobaltDim, 0.14, 0.04, 0.02, topper, 12);  // berry cluster
  } else { // banana
    const b = box(0.5, 0.18, 0.2, C.toast, 0, 0, 0, topper); b.rotation.z = 0.5;
  }

  // face on the cup
  sphere(0.05, C.cocoa, -0.14, 0.62, 0.34, g, 8);
  sphere(0.05, C.cocoa,  0.14, 0.62, 0.34, g, 8);
  const smile = cyl(0.09, 0.09, 0.04, C.cocoa, 0, 0.48, 0.36, g, 10);
  smile.rotation.x = Math.PI / 2; smile.scale.set(1.4, 1, 0.5);

  // stick arms
  const armL = cyl(0.03, 0.03, 0.4, C.limb, -0.42, 0.6, 0.1, g, 6); armL.rotation.z = 0.7;
  const armR = cyl(0.03, 0.03, 0.4, C.limb,  0.42, 0.6, 0.1, g, 6); armR.rotation.z = -0.7;

  // an instrument prop
  if (instrument === 'mic'){
    const stand = cyl(0.03, 0.03, 1.2, C.crustDark, 0, 0.6, 0.5, g, 8);
    sphere(0.09, C.cocoa, 0, 1.2, 0.5, g, 10);                // mic head
  } else if (instrument === 'keys'){
    const kb = box(0.9, 0.08, 0.3, C.crustDark, 0, 0.55, 0.5, g);
    box(0.9, 0.02, 0.28, C.white, 0, 0.6, 0.5, g);            // key strip
  } else { // guitar
    const body = box(0.34, 0.5, 0.12, accent, 0.25, 0.5, 0.42, g);
    body.rotation.z = 0.4;
    const neck = box(0.08, 0.7, 0.08, C.rye, -0.05, 0.75, 0.42, g);
    neck.rotation.z = 0.4;
  }

  return { group: g, armL, armR, baseY: 0, phaseOff: 0 };
}

export class YogurtBand {
  constructor(){
    this.root = new THREE.Group();
    this.members = [
      yogurtMember({ x: -1.6, fruit: 'blueberry',  accent: C.cobalt,    instrument: 'keys' }),
      yogurtMember({ x:  0.0, fruit: 'strawberry', accent: C.raspberry, instrument: 'mic'  }),
      yogurtMember({ x:  1.6, fruit: 'banana',     accent: C.tangerine, instrument: 'guitar' })
    ];
    this.members.forEach((m, i) => { m.phaseOff = i * (Math.PI * 2 / 3); this.root.add(m.group); });
  }

  // phase = fractional beats; energy 0..1 scales how hard they jam.
  update(dt, phase, energy = 0.4){
    const amp = 0.08 + energy * 0.16;
    this.members.forEach(m => {
      const t = phase * Math.PI * 2 + m.phaseOff;
      const hop = Math.max(0, Math.sin(t));
      m.group.position.y = m.baseY + hop * amp;
      m.group.rotation.z = Math.sin(t * 0.5) * 0.05 * (0.5 + energy);
      // arms pump on the beat
      m.armL.rotation.z =  0.7 - hop * 0.5 * (0.4 + energy);
      m.armR.rotation.z = -0.7 + hop * 0.5 * (0.4 + energy);
    });
  }
}
